export const useWebSpeechRecognition = (options = {}) => {
  const {
    language = 'vi-VN',
    continuous = true,
    interimResults = true,
    maxAlternatives = 1,
    appendMode = false,
    // Silence-based timeout: stop after this many ms with no speech
    timeout = 10000
  } = options

  const isSupported = ref(false)
  const isListening = ref(false)
  const isProcessing = ref(false)
  const transcript = ref('')
  const interimTranscript = ref('')
  const finalTranscript = ref('')
  const error = ref(null)
  const confidence = ref(0)

  let recognition = null
  // Only stop listening after a period of silence (no interim/final results)
  let silenceTimeoutId = null
  // Track intent and manual stop to enable auto-restart for continuous listening
  let intendedListening = false
  let manualStop = false

  const checkSupport = () => {
    if (typeof window === 'undefined') {
      isSupported.value = false
      return false
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    isSupported.value = !!SpeechRecognition
    return isSupported.value
  }

  const initializeRecognition = () => {
    if (!checkSupport()) {
      error.value = 'Trình duyệt không hỗ trợ nhận dạng giọng nói'
      return false
    }

    if (typeof window === 'undefined') {
      error.value = 'Không thể truy cập window object'
      return false
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    recognition = new SpeechRecognition()

    recognition.lang = language
    recognition.continuous = continuous
    recognition.interimResults = interimResults
    recognition.maxAlternatives = maxAlternatives

    recognition.onstart = () => {
      isListening.value = true
      isProcessing.value = false
      error.value = null
      manualStop = false

      // Start initial silence timer so session ends if user never speaks
      if (timeout > 0) {
        clearTimeout(silenceTimeoutId)
        silenceTimeoutId = setTimeout(() => {
          // Stop only if still intended to listen and no speech has occurred
          if (isListening.value && intendedListening) {
            intendedListening = false
            stop()
          }
        }, timeout)
      }
    }

    recognition.onresult = (event) => {
      let interim = ''
      let final = ''

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i]
        const transcriptText = result[0].transcript

        if (result.isFinal) {
          final += transcriptText
          confidence.value = result[0].confidence
        } else {
          interim += transcriptText
        }
      }

      interimTranscript.value = interim
      
      if (final) {
        finalTranscript.value = appendMode ? finalTranscript.value + final : final
        transcript.value = finalTranscript.value
      } else {
        transcript.value = appendMode 
          ? finalTranscript.value + interim 
          : interim
      }

      // Reset silence timer on any recognition activity (interim or final)
      if (timeout > 0) {
        clearTimeout(silenceTimeoutId)
        silenceTimeoutId = setTimeout(() => {
          if (isListening.value && intendedListening) {
            intendedListening = false
            stop()
          }
        }, timeout)
      }
    }

    recognition.onerror = (event) => {
      isProcessing.value = false
      
      switch (event.error) {
        case 'no-speech':
          error.value = 'Không phát hiện giọng nói. Vui lòng thử lại.'
          break
        case 'audio-capture':
          error.value = 'Không thể truy cập microphone. Vui lòng kiểm tra quyền truy cập.'
          break
        case 'not-allowed':
          error.value = 'Quyền truy cập microphone bị từ chối. Vui lòng cho phép truy cập microphone.'
          break
        case 'network':
          error.value = 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.'
          break
        case 'service-not-allowed':
          error.value = 'Dịch vụ nhận dạng giọng nói không khả dụng.'
          break
        case 'bad-grammar':
          error.value = 'Lỗi cấu hình ngữ pháp nhận dạng.'
          break
        case 'language-not-supported':
          error.value = 'Ngôn ngữ không được hỗ trợ.'
          break
        default:
          error.value = `Lỗi nhận dạng giọng nói: ${event.error}`
      }
      
      // Mark as not intended to keep listening to avoid auto-restart
      intendedListening = false
      stop()
    }

    recognition.onend = () => {
      // Some browsers end sessions frequently. If continuous listening was intended
      // and we didn't manually stop or hit the silence timeout, auto-restart.
      if (continuous && intendedListening && !manualStop && !error.value) {
        try {
          recognition.start()
          return
        } catch (e) {
          // If restart fails, fall through to cleanup
        }
      }

      isListening.value = false
      isProcessing.value = false

      clearTimeout(silenceTimeoutId)
    }

    return true
  }

  const start = async () => {
    if (isListening.value) {
      stop()
      return
    }

    if (!recognition && !initializeRecognition()) {
      return
    }

    try {
      isProcessing.value = true
      error.value = null
      interimTranscript.value = ''
      intendedListening = true
      manualStop = false

      if (!appendMode) {
        finalTranscript.value = ''
        transcript.value = ''
      }

      recognition.start()
    } catch (err) {
      isProcessing.value = false
      error.value = 'Không thể khởi động nhận dạng giọng nói: ' + err.message
    }
  }

  const stop = () => {
    intendedListening = false
    manualStop = true
    if (recognition && isListening.value) {
      recognition.stop()
    }
    
    clearTimeout(silenceTimeoutId)
    isListening.value = false
    isProcessing.value = false
  }

  const abort = () => {
    if (recognition) {
      recognition.abort()
    }
    
    clearTimeout(silenceTimeoutId)
    isListening.value = false
    isProcessing.value = false
    error.value = 'Nhận dạng giọng nói đã bị hủy'
  }

  const reset = () => {
    stop()
    transcript.value = ''
    interimTranscript.value = ''
    finalTranscript.value = ''
    error.value = null
    confidence.value = 0
  }

  const toggle = () => {
    if (isListening.value) {
      stop()
    } else {
      start()
    }
  }

  onUnmounted(() => {
    stop()
    if (recognition) {
      recognition = null
    }
  })

  if (process.client) {
    checkSupport()
  }

  return {
    isSupported: readonly(isSupported),
    isListening: readonly(isListening),
    isProcessing: readonly(isProcessing),
    transcript: readonly(transcript),
    interimTranscript: readonly(interimTranscript),
    finalTranscript: readonly(finalTranscript),
    error: readonly(error),
    confidence: readonly(confidence),
    start,
    stop,
    abort,
    reset,
    toggle
  }
}
